import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { isServerSideCheckoutEligible } from "../../utils/productUtils.js";
import { getRetailerFromId, formatMoney } from "../../utils/stringUtils.js";
import numeral from "numeral";
import { useState, useCallback } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../../Components/ui/carousel.js";
import { ProductVariantPicker } from "./ProductVariantPicker.js";
import { FeaturedProductWidget as FeaturedProductWidget$1 } from "@brandclub/bc-widgets";
import { useProductClickTracking } from "../hooks/useProductClickTracking.js";
const getFontWeight = (weight) => {
  const weightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return weightMap[weight] || 500;
};
const ProductImageCarousel = ({ images, productTitle, state, resolveColor }) => {
  if (images.length === 0) return null;
  const imageContainerStyle = {
    position: "relative",
    width: "100%",
    aspectRatio: "1/1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: resolveColor(state.imageBgColor),
    overflow: "hidden"
  };
  const imageWrapperStyle = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: state.imagePadding
  };
  const imageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain"
  };
  if (images.length === 1) {
    return /* @__PURE__ */ jsx("div", { style: imageContainerStyle, children: /* @__PURE__ */ jsx("div", { style: imageWrapperStyle, children: /* @__PURE__ */ jsx("img", { src: images[0], alt: productTitle, style: imageStyle, loading: "eager" }) }) });
  }
  return /* @__PURE__ */ jsx("div", { style: imageContainerStyle, className: "tw:group", children: /* @__PURE__ */ jsxs(
    Carousel,
    {
      opts: {
        align: "start",
        loop: false
      },
      className: "tw:w-full tw:h-full",
      children: [
        /* @__PURE__ */ jsx(CarouselContent, { className: "tw:h-full [&>div]:h-full", children: images.map((imageUrl, idx) => /* @__PURE__ */ jsx(CarouselItem, { className: "tw:h-full", children: /* @__PURE__ */ jsx("div", { style: imageWrapperStyle, children: /* @__PURE__ */ jsx(
          "img",
          {
            src: imageUrl,
            alt: `${productTitle} - ${idx + 1}`,
            style: imageStyle,
            loading: idx === 0 ? "eager" : "lazy"
          }
        ) }) }, idx)) }),
        images.length > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            CarouselPrevious,
            {
              className: "tw:left-2 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity",
              style: { width: state.carouselArrowSize, height: state.carouselArrowSize }
            }
          ),
          /* @__PURE__ */ jsx(
            CarouselNext,
            {
              className: "tw:right-2 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity",
              style: { width: state.carouselArrowSize, height: state.carouselArrowSize }
            }
          )
        ] })
      ]
    }
  ) });
};
const StarIcon = ({ color = "#ED792F" }) => {
  return /* @__PURE__ */ jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M15.9773 6.07492C15.9519 5.98927 15.9029 5.9125 15.836 5.85339C15.769 5.79428 15.6867 5.75522 15.5986 5.7407L10.6386 4.98515L8.41638 0.274035C8.38041 0.192515 8.32151 0.12321 8.24687 0.074557C8.17222 0.0259036 8.08504 0 7.99593 0C7.90683 0 7.81965 0.0259036 7.745 0.074557C7.67035 0.12321 7.61146 0.192515 7.57549 0.274035L5.35327 4.98515L0.393265 5.7407C0.305129 5.75522 0.222865 5.79428 0.155895 5.85339C0.088925 5.9125 0.0399567 5.98927 0.0145984 6.07492C-0.012732 6.16181 -0.0161775 6.25445 0.00462334 6.34313C0.0254242 6.43181 0.0697048 6.51325 0.132821 6.57892L3.73371 10.2465L2.88393 15.4251C2.86874 15.5147 2.87808 15.6067 2.91094 15.6913C2.94381 15.776 2.99898 15.8502 3.0706 15.906C3.14023 15.9597 3.22403 15.9918 3.31168 15.9984C3.39934 16.0051 3.48702 15.9859 3.56393 15.9434L8.00038 13.498L12.4368 15.9434C12.5137 15.9859 12.6014 16.0051 12.6891 15.9984C12.7767 15.9918 12.8605 15.9597 12.9302 15.906C13.0018 15.8502 13.0569 15.776 13.0898 15.6913C13.1227 15.6067 13.132 15.5147 13.1168 15.4251L12.267 10.2465L15.859 6.5807C15.9225 6.51486 15.9669 6.43311 15.9878 6.34408C16.0086 6.25505 16.0049 6.16206 15.9773 6.07492Z",
      fill: color
    }
  ) });
};
const FeaturedProductWidget = ({
  product,
  onAddToCart,
  onOpenCart,
  onVariantSelect
}) => {
  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const { trackProductClick } = useProductClickTracking();
  const handleProductClick = useCallback(() => {
    trackProductClick({
      stacklineSku: product.stacklineSku,
      productTitle: product.title,
      clickSource: "featured",
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      hasReward: product.hasRewards,
      rewardAmount: product.totalRewardAmount
    });
  }, [trackProductClick, product]);
  const images = product.imageUrls || [];
  const featureBullets = product.featureBullets || [];
  const variants = product.variants || [];
  const variationNameValues = product.variationNameValues || {};
  const getReviewRatingValue = () => {
    const formatValue = (value) => Math.round(value * 10) / 10;
    if (product.reviewsRating && product.reviewsRating > 0) {
      return formatValue(product.reviewsRating);
    }
    if (product.reviewsAverageRating && product.reviewsAverageRating > 0) {
      return formatValue(product.reviewsAverageRating);
    }
    return 0;
  };
  const handleAddToBag = async () => {
    const eligibility = product.checkoutEligibility;
    if (!onAddToCart) {
      handleViewInStore();
      return;
    }
    if (isServerSideCheckoutEligible(eligibility) && !isAddingToBag) {
      setIsAddingToBag(true);
      try {
        await onAddToCart(product);
        onOpenCart == null ? void 0 : onOpenCart();
      } catch (error) {
        console.error("Error adding to bag:", error);
      } finally {
        setIsAddingToBag(false);
      }
    }
  };
  const handleViewInStore = () => {
    handleProductClick();
    const retailerUrl = product.retailerProductPageUrl;
    if (retailerUrl) {
      window.open(retailerUrl, "_blank");
    }
  };
  const reviewRating = getReviewRatingValue();
  const reviewCount = product.reviewsCount || 0;
  const retailerId = product.retailerId || 0;
  const retailerName = product.retailerDisplayName || getRetailerFromId(retailerId);
  const price = product.retailPrice || 0;
  const isEligibleForCheckout = isServerSideCheckoutEligible(product.checkoutEligibility);
  const getVariationLabel = () => {
    if (!variationNameValues || Object.keys(variationNameValues).length === 0) return null;
    const priorityKeys = ["sizeName", "colorName", "styleName", "patternName"];
    for (const key of priorityKeys) {
      if (variationNameValues[key]) {
        const dimension = key.replace(/Name$/, "").replace(/([A-Z])/g, " $1").trim();
        return {
          dimension: dimension.charAt(0).toUpperCase() + dimension.slice(1),
          value: variationNameValues[key]
        };
      }
    }
    const firstKey = Object.keys(variationNameValues)[0];
    if (firstKey) {
      const dimension = firstKey.replace(/Name$/, "").replace(/([A-Z])/g, " $1").trim();
      return {
        dimension: dimension.charAt(0).toUpperCase() + dimension.slice(1),
        value: variationNameValues[firstKey]
      };
    }
    return null;
  };
  const variationLabel = getVariationLabel();
  const showVariants = variants.length > 0 && onVariantSelect;
  return /* @__PURE__ */ jsx(FeaturedProductWidget$1, { widgetId: "featured-product", displayName: "Featured Product", children: (state, resolveColor) => /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        gap: state.containerGap,
        backgroundColor: resolveColor(state.containerBgColor)
      },
      children: [
        /* @__PURE__ */ jsx(
          "h1",
          {
            style: {
              fontSize: state.titleFontSize,
              fontWeight: getFontWeight(state.titleFontWeight),
              color: resolveColor(state.titleColor),
              letterSpacing: state.titleLetterSpacing,
              margin: 0
            },
            children: product.title
          }
        ),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "featured-product-grid",
            style: { display: "grid", width: "100%", margin: "8px 0", backgroundColor: "#ffffff" },
            children: [
              images.length > 0 && /* @__PURE__ */ jsx(
                ProductImageCarousel,
                {
                  images,
                  productTitle: product.title,
                  state,
                  resolveColor
                }
              ),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  style: { display: "flex", flexDirection: "column", gap: state.contentGap, padding: state.contentPadding },
                  children: [
                    reviewRating > 0 && /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: state.ratingFontSize,
                          fontWeight: 500
                        },
                        children: [
                          /* @__PURE__ */ jsx(StarIcon, { color: resolveColor(state.ratingStarColor) }),
                          /* @__PURE__ */ jsxs("span", { style: { color: resolveColor(state.ratingTextColor), paddingTop: 2 }, children: [
                            reviewRating,
                            " (",
                            numeral(reviewCount).format("0,0"),
                            " ratings)"
                          ] })
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        style: {
                          fontSize: state.priceFontSize,
                          fontWeight: getFontWeight(state.priceFontWeight),
                          color: resolveColor(state.priceColor),
                          letterSpacing: state.priceLetterSpacing
                        },
                        children: formatMoney(price)
                      }
                    ),
                    showVariants && /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: [
                      variationLabel && /* @__PURE__ */ jsxs(
                        "p",
                        {
                          style: {
                            fontSize: 16,
                            fontWeight: 500,
                            color: resolveColor(state.titleColor),
                            letterSpacing: 0.16,
                            margin: 0
                          },
                          children: [
                            variationLabel.dimension,
                            ": ",
                            /* @__PURE__ */ jsx("span", { style: { fontWeight: 600 }, children: variationLabel.value })
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        ProductVariantPicker,
                        {
                          currentStacklineSku: product.stacklineSku,
                          variants,
                          onVariantSelect
                        }
                      )
                    ] }),
                    featureBullets.length > 0 && /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: [
                      /* @__PURE__ */ jsx(
                        "h4",
                        {
                          style: {
                            fontSize: state.featureTitleFontSize,
                            fontWeight: 600,
                            color: resolveColor(state.featureTitleColor),
                            letterSpacing: 0.08,
                            margin: 0
                          },
                          children: "Key features"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        "ul",
                        {
                          style: {
                            margin: 0,
                            paddingLeft: 18,
                            listStyleType: "disc",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2
                          },
                          children: featureBullets.slice(0, 3).map((bullet, idx) => /* @__PURE__ */ jsx(
                            "li",
                            {
                              style: {
                                fontSize: state.featureBulletFontSize,
                                fontWeight: 500,
                                color: resolveColor(state.featureBulletColor),
                                lineHeight: "20px"
                              },
                              children: bullet
                            },
                            idx
                          ))
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: 12, marginTop: "auto" }, children: /* @__PURE__ */ jsxs(
                      "button",
                      {
                        onClick: handleAddToBag,
                        disabled: isAddingToBag,
                        style: {
                          width: "100%",
                          height: state.primaryButtonHeight,
                          backgroundColor: resolveColor(state.primaryButtonBgColor),
                          color: resolveColor(state.primaryButtonTextColor),
                          border: "none",
                          borderRadius: state.primaryButtonBorderRadius,
                          fontSize: state.primaryButtonFontSize,
                          fontWeight: getFontWeight(state.primaryButtonFontWeight),
                          letterSpacing: 0.08,
                          cursor: isAddingToBag ? "not-allowed" : "pointer",
                          opacity: isAddingToBag ? 0.7 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 16,
                          transition: "all 0.2s"
                        },
                        children: [
                          /* @__PURE__ */ jsx("span", { children: isEligibleForCheckout ? "Add to bag" : "View" }),
                          /* @__PURE__ */ jsx(
                            "span",
                            {
                              style: {
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: resolveColor(state.primaryButtonTextColor)
                              }
                            }
                          ),
                          /* @__PURE__ */ jsxs("span", { children: [
                            retailerName,
                            " ",
                            formatMoney(price)
                          ] })
                        ]
                      }
                    ) })
                  ]
                }
              )
            ]
          }
        )
      ]
    }
  ) });
};
export {
  FeaturedProductWidget
};
//# sourceMappingURL=FeaturedProductWidget.js.map
